import React from "react";
import * as Icons from "lucide-react";
import { FEATURE_GROUPS, FEATURE_GRID, INTEGRATIONS, FINAL_CTA } from "../mock";
import { Seo } from "../components/Seo";
import { PageHero, Reveal, SectionHeader, CheckItem, DarkCTA } from "../components/Shared";
import RouteMapIllustration from "../components/RouteMapIllustration";
import FleetMapIllustration from "../components/FleetMapIllustration";
import DriverAppIllustration from "../components/DriverAppIllustration";

const ILLUSTRATIONS = {
  "route-map": RouteMapIllustration,
  "fleet-map": FleetMapIllustration,
  "driver-app": DriverAppIllustration,
};

const Features = () => (
  <>
    <Seo title="Features — Fleet tracking, route planning, auto dispatch & proof of delivery" description="Fleet tracking, route planning, auto dispatch, a driver app and proof of delivery — designed as one connected product." />
    <PageHero
      eyebrow="Features"
      title="Everything between the order and the doorstep."
      subtitle="Fleet tracking, route planning, auto dispatch, a driver app and proof of delivery — designed as one connected product."
      primary={{ label: "Request a demo", to: "/contact" }}
      secondary={{ label: "See pricing", to: "/pricing" }}
    />

    {FEATURE_GROUPS.map((g, i) => (
      <section
        key={g.id}
        id={g.id}
        className={`section ${i % 2 === 1 ? "bg-[var(--fr-bg-alt)]" : ""}`}
        data-testid={`feature-group-${g.id}`}
      >
        <div className={`container-x grid md:grid-cols-2 gap-12 items-center`}>
          <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
            <span className="eyebrow">{g.eyebrow}</span>
            <h2 className="h-section mt-4">{g.title}</h2>
            <p className="mt-5 text-lg text-[var(--fr-ink-2)] leading-relaxed">{g.text}</p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {g.bullets.map((b) => (
                <CheckItem key={b}>{b}</CheckItem>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className={`card card-hover p-3 ${i % 2 === 1 ? "md:order-1" : ""}`}>
            <div className="img-tile aspect-[4/3] bg-white">
              {ILLUSTRATIONS[g.illustration] ? (
                React.createElement(ILLUSTRATIONS[g.illustration], { className: "w-full h-full" })
              ) : (
                <img src={g.image} alt={g.title} loading="lazy" />
              )}
            </div>
          </Reveal>
        </div>
      </section>
    ))}

    <section className="section" data-testid="feature-grid">
      <div className="container-x">
        <SectionHeader
          eyebrow="Complete operational control"
          title="Every module, one login"
          subtitle="Manage dispatch, merchant portals, communications and reporting, all tied to real-time delivery execution data."
        />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURE_GRID.map((f, i) => {
            const Icon = Icons[f.icon] || Icons.Circle;
            return (
              <Reveal key={f.title} delay={(i % 4) * 60} className="card card-hover p-6">
                <div className="w-10 h-10 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <h3 className="font-display font-bold mt-4">{f.title}</h3>
                <p className="mt-2 text-sm text-[var(--fr-ink-2)] leading-relaxed">{f.text}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section id="integrations" className="section bg-[var(--fr-bg-alt)]" data-testid="integrations">
      <div className="container-x">
        <SectionHeader
          eyebrow="Integrations"
          title="Plugs into the systems you already run"
          subtitle="A single operational flow for orders, tracking, proof and reporting — connected directly to your ERP or e-commerce stack."
        />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {INTEGRATIONS.map((n, i) => (
            <Reveal key={n} delay={(i % 4) * 60} className="card card-hover h-24 flex items-center justify-center font-display font-bold text-[var(--fr-ink)]/70 text-center px-4">
              {n}
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-[var(--fr-muted)]">Plus a REST API and webhooks for everything else.</p>
      </div>
    </section>

    <DarkCTA title={FINAL_CTA.title} text={FINAL_CTA.text} cta={FINAL_CTA.cta} email={FINAL_CTA.email} />
  </>
);

export default Features;
